import { AccessPoint } from '@kinet-open-network/client/access-points/data-access';
import {
  ApiDateFormat,
  BaseModel,
} from '@kinet-open-network/client/shared/data-access/model';
import {
  AbilitySubjectType,
  CustomerAdditionalProductEntityType,
  DiscountType,
  getCustomerAdditionalProductPriceType,
  ICustomerAdditionalProduct,
  ICustomerAdditionalProductEntity,
  IItemPrice,
  PaymentType,
} from '@kinet-open-network/shared/data-access/model';
import { Type } from 'class-transformer';
import { endOfDay, startOfDay } from 'date-fns';

export class CustomerAdditionalProduct
  extends BaseModel<CustomerAdditionalProduct, 'CustomerAdditionalProduct'>
  implements ICustomerAdditionalProduct
{
  static override readonly modelName: AbilitySubjectType =
    'CustomerAdditionalProduct';

  entityType!: CustomerAdditionalProductEntityType;
  entityId!: string;
  payment!: PaymentType;
  entity!: ICustomerAdditionalProductEntity | null;
  @Type(() => Number)
  count!: number;
  @Type(() => Number)
  customerId!: number;
  apId!: string;
  @Type(() => AccessPoint)
  accessPoint!: AccessPoint | null;
  @Type(() => Date)
  activationDate!: Date | null;
  @Type(() => Date)
  deactivationDate!: Date | null;
  invoiceId!: string | null;
  @Type(() => Date)
  invoiceDate!: Date | null;
  @Type(() => Date)
  invoiceFrom!: Date | null;
  @Type(() => Date)
  invoiceTo!: Date | null;
  discountType!: DiscountType | null;
  @Type(() => Number)
  discountValue!: number | null;
  @ApiDateFormat('yyyy-MM-dd')
  @Type(() => Date)
  discountTo!: Date | null;
  closeHash!: string | null;

  get invoiced(): boolean {
    return !!this.invoiceDate;
  }

  get price(): number {
    return this.getPriceAt(new Date());
  }

  get unit(): string {
    return this.entity?.unit ?? 'ks';
  }

  get deactivated() {
    return !!this.deactivationDate && this.deactivationDate <= new Date();
  }

  getActivePriceAt(date: Date | number = Date.now()): IItemPrice | null {
    const targetType = getCustomerAdditionalProductPriceType(
      this.entityType,
      this.payment,
    );
    const targetDate = new Date(date);
    const activePrice = this.entity?.prices?.find(
      (price) =>
        price.type === targetType &&
        startOfDay(new Date(price.validFrom)) <= targetDate &&
        (!price.validTo || endOfDay(new Date(price.validTo)) >= targetDate),
    );

    return activePrice ?? null;
  }

  hasDiscountAt(date: Date | number = new Date()): boolean {
    return (
      !!this.discountValue &&
      (!this.discountTo || endOfDay(this.discountTo) > new Date(date))
    );
  }

  getDiscountAt(date: Date | number = new Date()): number {
    if (!this.hasDiscountAt(date)) {
      return 0;
    }

    const priceValue = this.getActivePriceAt(date)?.price ?? 0;

    switch (this.discountType) {
      case DiscountType.Percentage:
        return (
          (priceValue * Math.min(100, Math.max(0, this.discountValue ?? 0))) /
          100
        );
      case DiscountType.Overwrite:
        // discountValue is the new overwritten price; discount = difference
        return Math.max(0, priceValue - (this.discountValue ?? 0));
      case DiscountType.Fixed:
        return Math.min(priceValue, this.discountValue ?? 0);
      default:
        return 0;
    }
  }

  getPriceAt(date: Date | number = new Date()): number {
    const price = this.getActivePriceAt(date)?.price ?? 0;

    if (this.hasDiscountAt(date)) {
      return price - this.getDiscountAt(date);
    }

    return price;
  }
}
